export const CONVERSION_EVENTS = {
    SONG: 'Song Click',
    PROJECT: 'Project Click',
    PLAYLIST: 'Playlist Click',
    SPOTIFY_PROFILE: 'Spotify Profile Click',
    INSTAGRAM_PROFILE: 'Instagram Profile Click',
    YOUTUBE_VIDEO: 'YouTube Video Click',
    YOUTUBE_CHANNEL: 'YouTube Channel Click',
    CUSTOM_LINK: 'Custom Link Click',
    MERCH_STORE: 'Merch Store Click',
    TICKET: 'Ticket Click',
    BIO_LINK: 'Bio Link Click',
    PRE_SAVE: 'Pre-save',
};

const PRE_SAVE_LINK_TYPES = new Set(['presave', 'preadd', 'pre_release', 'future_release']);

const getUrlHostname = (value) => {
    if (!value || typeof value !== 'string') return '';
    try {
        const trimmed = value.trim();
        const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        return new URL(href).hostname.replace(/^www\./i, '').toLowerCase();
    } catch {
        return '';
    }
};

export const isUpncomerFeatureFmUrl = (value) => {
    const host = getUrlHostname(value);
    if (!host) return false;
    return host === 'upnc.me' || host === 'feature.fm' || host.endsWith('.feature.fm');
};

const hostMatches = (value, hosts) => {
    const host = getUrlHostname(value);
    if (!host) return false;
    return hosts.some((allowed) => host === allowed || host.endsWith(`.${allowed}`));
};

const isSpotifyUrl = (value) =>
    hostMatches(value, ['spotify.com']) || getUrlHostname(value) === 'spotify.link';

const isInstagramUrl = (value) =>
    hostMatches(value, ['instagram.com']) || getUrlHostname(value) === 'instagr.am';

const isYoutubeUrl = (value) =>
    hostMatches(value, ['youtube.com']) || getUrlHostname(value) === 'youtu.be';

const getMusicConversionEvent = (trackType) => {
    const type = (trackType || '').toLowerCase();
    if (type === 'album') return CONVERSION_EVENTS.PROJECT;
    if (type === 'playlist') return CONVERSION_EVENTS.PLAYLIST;
    return CONVERSION_EVENTS.SONG;
};

const getYoutubeConversionEvent = (contentType) =>
    contentType === 'channel' ? CONVERSION_EVENTS.YOUTUBE_CHANNEL : CONVERSION_EVENTS.YOUTUBE_VIDEO;

const getEventFromTargetObject = (target = {}) => {
    if (target.targetType === 'song') return getMusicConversionEvent(target.trackType);
    if (target.targetType === 'spotifyProfile') return CONVERSION_EVENTS.SPOTIFY_PROFILE;
    if (target.targetType === 'instaProfile') return CONVERSION_EVENTS.INSTAGRAM_PROFILE;
    if (target.targetType === 'youtubeContent') return getYoutubeConversionEvent(target.contentType);
    if (target.targetName === 'Bio Link Promotion') return CONVERSION_EVENTS.BIO_LINK;
    if (target.targetName === 'Sell Tickets') return CONVERSION_EVENTS.TICKET;
    if (target.targetName === 'Sell Merch') return CONVERSION_EVENTS.MERCH_STORE;
    return CONVERSION_EVENTS.CUSTOM_LINK;
};

const getFeatureFmConversionEvent = (target = {}, linkType) => {
    const type = String(linkType || target.linkType || target.featureFmType || '').toLowerCase();
    if (PRE_SAVE_LINK_TYPES.has(type)) return CONVERSION_EVENTS.PRE_SAVE;
    if (type === 'biolink' || target.targetName === 'Bio Link Promotion') return CONVERSION_EVENTS.BIO_LINK;
    if (type === 'tourlink' || target.targetName === 'Sell Tickets') return CONVERSION_EVENTS.TICKET;
    if (target.targetType === 'song') return getMusicConversionEvent(target.trackType);
    return getEventFromTargetObject(target);
};

const isIntendedFeatureFmDestination = (target = {}) => {
    if (target.targetType === 'song') return true;
    if (target.targetName === 'Bio Link Promotion' || target.targetName === 'Sell Tickets') return true;
    return Boolean(target.destinationUrlTrackId || target.linkId);
};

const getExternalConversionEvent = (target = {}, url) => {
    if (target.targetType === 'instaProfile') {
        return !url || isInstagramUrl(url)
            ? CONVERSION_EVENTS.INSTAGRAM_PROFILE
            : CONVERSION_EVENTS.CUSTOM_LINK;
    }
    if (target.targetType === 'youtubeContent') {
        return !url || isYoutubeUrl(url)
            ? getYoutubeConversionEvent(target.contentType)
            : CONVERSION_EVENTS.CUSTOM_LINK;
    }
    if (target.targetType === 'spotifyProfile') {
        return !url || isSpotifyUrl(url)
            ? CONVERSION_EVENTS.SPOTIFY_PROFILE
            : CONVERSION_EVENTS.CUSTOM_LINK;
    }
    if (target.targetName === 'Sell Merch') return CONVERSION_EVENTS.MERCH_STORE;
    return CONVERSION_EVENTS.CUSTOM_LINK;
};

export const getFinalDestinationUrl = (campaign, destinationUrl) => {
    const target = campaign?.targetObject || {};
    return destinationUrl || target.youtubeUrl || target.spotifyProfileUrl || '';
};

/**
 * Conversion Event is based on the final destination, not the original objective.
 * UpNComer Feature.fm pages keep their Feature.fm event. Any other URL uses the
 * matching platform event, or Custom Link Click when the URL was overridden.
 */
export const getCampaignConversionEvent = ({ campaign, destinationUrl, linkType } = {}) => {
    if (!campaign?.targetObject && !destinationUrl) return '';

    const target = campaign?.targetObject || {};
    const url = getFinalDestinationUrl(campaign, destinationUrl);

    if (isUpncomerFeatureFmUrl(url) || (!url && isIntendedFeatureFmDestination(target))) {
        return getFeatureFmConversionEvent(target, linkType);
    }

    if (!url) return getEventFromTargetObject(target);

    return getExternalConversionEvent(target, url);
};

const PERFORMANCE_COPY = {
    [CONVERSION_EVENTS.SONG]: {
        goal: 'Drive streams and engagement for your song',
        title: 'Conversions',
        countLabel: 'Song Streams',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to stream your song.',
    },
    [CONVERSION_EVENTS.PROJECT]: {
        goal: 'Drive streams and engagement for your project',
        title: 'Conversions',
        countLabel: 'Project Streams',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to stream your project.',
    },
    [CONVERSION_EVENTS.PLAYLIST]: {
        goal: 'Drive listeners to your playlist',
        title: 'Conversions',
        countLabel: 'Playlist Listeners',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to your playlist.',
    },
    [CONVERSION_EVENTS.SPOTIFY_PROFILE]: {
        goal: 'Grow your Spotify audience and engagement',
        title: 'Conversions',
        countLabel: 'Spotify Profile Visits',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to your Spotify profile.',
    },
    [CONVERSION_EVENTS.INSTAGRAM_PROFILE]: {
        goal: 'Grow your Instagram audience and engagement',
        title: 'Conversions',
        countLabel: 'Instagram Profile Visits',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to your Instagram profile.',
    },
    [CONVERSION_EVENTS.YOUTUBE_VIDEO]: {
        goal: 'Drive YouTube views and engagement',
        title: 'Conversions',
        countLabel: 'YouTube Video Viewers',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to your YouTube video.',
    },
    [CONVERSION_EVENTS.YOUTUBE_CHANNEL]: {
        goal: 'Grow YouTube subscribers and engagement',
        title: 'Conversions',
        countLabel: 'YouTube Channel Visits',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to your YouTube channel.',
    },
    [CONVERSION_EVENTS.TICKET]: {
        goal: 'Sell tickets to your upcoming shows',
        title: 'Conversions',
        countLabel: 'Potential Ticket Buyers',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to a ticket destination.',
    },
    [CONVERSION_EVENTS.BIO_LINK]: {
        goal: 'Turn visibility into real fans',
        title: 'Conversions',
        countLabel: 'Engaged Bio Visitors',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to a destination from your bio page.',
    },
    [CONVERSION_EVENTS.MERCH_STORE]: {
        goal: 'Increase sales of your merch',
        title: 'Conversions',
        countLabel: 'Store Visits',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to your merch store.',
    },
    [CONVERSION_EVENTS.CUSTOM_LINK]: {
        goal: 'Drive traffic to your link',
        title: 'Conversions',
        countLabel: 'Link Visits',
        costLabel: 'Cost per Conversion',
        description: 'people clicked through to your destination.',
    },
    [CONVERSION_EVENTS.PRE_SAVE]: {
        goal: 'Drive pre-saves for your upcoming release',
        title: 'Conversions',
        countLabel: 'Pre-saves',
        costLabel: 'Cost per Conversion',
        description: 'people pre-saved your release.',
    },
};

const DEFAULT_PERFORMANCE_COPY = {
    goal: 'Reach your campaign goal',
    title: 'Conversions',
    countLabel: 'Link Visits',
    costLabel: 'Cost per Conversion',
    description: 'people clicked through to your destination.',
};

export const getCampaignPerformanceCopy = (conversionEvent) =>
    PERFORMANCE_COPY[conversionEvent] || DEFAULT_PERFORMANCE_COPY;

export const TRACKING_SOURCES = {
    FEATURE_FM: 'feature_fm',
    META: 'meta',
};

export const UNAVAILABLE_METRIC = '—';

export const isFeatureFmTrackedDestination = ({ campaign, destinationUrl } = {}) => {
    const target = campaign?.targetObject || {};
    const url = getFinalDestinationUrl(campaign, destinationUrl);
    return isUpncomerFeatureFmUrl(url) || (!url && isIntendedFeatureFmDestination(target));
};

export const getConversionTrackingSource = ({ campaign, destinationUrl, hasFeatureFmAccess = true } = {}) => {
    if (hasFeatureFmAccess === false) {
        return TRACKING_SOURCES.META;
    }

    if (isFeatureFmTrackedDestination({ campaign, destinationUrl })) {
        return TRACKING_SOURCES.FEATURE_FM;
    }

    return TRACKING_SOURCES.META;
};

const toNullableNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const getFeatureFmConversionCount = (smartLinkAnalytics) => {
    if (smartLinkAnalytics?.status === 'error') {
        return { conversions: null, available: false };
    }

    const raw = smartLinkAnalytics?.totals?.clicksToService;

    if (smartLinkAnalytics?.status === 'ok') {
        return { conversions: toNullableNumber(raw) ?? 0, available: true };
    }

    if (raw === undefined || raw === null || raw === '') {
        return { conversions: null, available: false };
    }

    return { conversions: toNullableNumber(raw) ?? 0, available: true };
};

const getMetaDestinationClickCount = (actions, insights) => {
    if (insights?.available === false) {
        return { conversions: null, available: false };
    }

    if (!insights && !actions) {
        return { conversions: null, available: false };
    }

    const raw = actions?.link_click ?? actions?.outbound_click;
    if (raw === undefined || raw === null || raw === '') {
        return { conversions: 0, available: true };
    }

    return { conversions: toNullableNumber(raw) ?? 0, available: true };
};

export const getCampaignConversionMetrics = ({
    campaign,
    destinationUrl,
    smartLinkAnalytics,
    actions,
    insights,
    linkType,
    hasFeatureFmAccess = true,
} = {}) => {
    const source = getConversionTrackingSource({ campaign, destinationUrl, hasFeatureFmAccess });
    const conversionEvent = getCampaignConversionEvent({
        campaign,
        destinationUrl,
        linkType: linkType || smartLinkAnalytics?.linkType,
    });
    const spend = insights?.available === false ? null : toNullableNumber(insights?.spend);
    const counted = source === TRACKING_SOURCES.FEATURE_FM
        ? (conversionEvent === CONVERSION_EVENTS.TICKET
            ? getFeatureFmTicketConversionCount(smartLinkAnalytics)
            : conversionEvent === CONVERSION_EVENTS.PRE_SAVE
                ? getFeatureFmPreSaveConversionCount(smartLinkAnalytics)
                : getFeatureFmConversionCount(smartLinkAnalytics))
        : getMetaDestinationClickCount(actions, insights);

    const costPerConversion = counted.available && counted.conversions > 0 && spend !== null
        ? spend / counted.conversions
        : counted.available && counted.conversions === 0
            ? 0
            : null;

    return {
        source,
        conversions: counted.conversions,
        conversionsAvailable: counted.available,
        costPerConversion,
    };
};

export const formatConversionMetric = (value, available) => (
    available ? value : UNAVAILABLE_METRIC
);

export const isMetaInsightsAvailable = (insights) =>
    Boolean(insights) && insights.available !== false;

export const formatInsightMetric = (value, available) => {
    if (!available) return UNAVAILABLE_METRIC;
    if (value === null || value === undefined || value === '') return UNAVAILABLE_METRIC;
    return value;
};

export const formatInsightCurrency = (value, available, currency, digits = 0) => {
    if (!available) return UNAVAILABLE_METRIC;
    const amount = Number(value || 0);
    return `${currency} ${digits > 0 ? amount.toFixed(digits) : amount}`.trim();
};

export const formatCostPerConversion = (value, available, currency) => {
    if (!available) return UNAVAILABLE_METRIC;
    return `${currency} ${Number(value || 0).toFixed(4)}`;
};

const IGNORED_FEATURE_FM_DESTINATIONS = new Set(['custom_branding']);

export const normalizeFeatureFmDestinationKey = (name = '') =>
    String(name)
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

const TICKET_DESTINATION_KEYS = new Set([
    'ticketmaster',
    'ticket_master',
    'bandcamp',
    'eventbrite',
    'songkick',
    'song_kick',
    'ticketfly',
    'ticket_fly',
    'tourdates',
    'tourdate',
    'tour_date',
    'tour_dates',
]);

export const isTicketDestinationKey = (name = '') => {
    const key = normalizeFeatureFmDestinationKey(name);
    if (!key) return false;
    const compact = key.replace(/_/g, '');
    return TICKET_DESTINATION_KEYS.has(key) || TICKET_DESTINATION_KEYS.has(compact);
};

const getFeatureFmTicketConversionCount = (smartLinkAnalytics) => {
    if (smartLinkAnalytics?.status === 'error') {
        return { conversions: null, available: false };
    }

    if (smartLinkAnalytics?.status !== 'ok' && !Array.isArray(smartLinkAnalytics?.streams)) {
        return { conversions: null, available: false };
    }

    const conversions = getFeatureFmDestinationBreakdown(smartLinkAnalytics)
        .filter((destination) => destination.isTicket)
        .reduce((sum, destination) => sum + destination.clicks, 0);

    return { conversions, available: true };
};

const getFeatureFmPreSaveConversionCount = (smartLinkAnalytics) => {
    if (smartLinkAnalytics?.status === 'error') {
        return { conversions: null, available: false };
    }

    if (smartLinkAnalytics?.status !== 'ok') {
        return { conversions: null, available: false };
    }

    const raw = smartLinkAnalytics?.preSaves?.total;
    if (raw === undefined || raw === null) {
        return { conversions: null, available: false };
    }

    return { conversions: toNullableNumber(raw) ?? 0, available: true };
};

export const getFeatureFmDestinationBreakdown = (smartLinkAnalytics, conversionEvent) => {
    if (conversionEvent === CONVERSION_EVENTS.PRE_SAVE && smartLinkAnalytics?.preSaves) {
        const destinations = (smartLinkAnalytics.preSaves.byService || [])
            .filter((service) => {
                const key = normalizeFeatureFmDestinationKey(service?.name);
                return key && !IGNORED_FEATURE_FM_DESTINATIONS.has(key);
            })
            .map((service) => {
                const key = normalizeFeatureFmDestinationKey(service.name);
                return {
                    key,
                    name: String(service.name),
                    clicks: toNullableNumber(service.count) ?? 0,
                    isTicket: false,
                };
            });

        const total = destinations.reduce((sum, destination) => sum + destination.clicks, 0);

        return destinations
            .sort((a, b) => b.clicks - a.clicks)
            .map((destination) => ({
                ...destination,
                percent: total > 0 ? (destination.clicks / total) * 100 : 0,
            }));
    }

    const streams = Array.isArray(smartLinkAnalytics?.streams) ? smartLinkAnalytics.streams : [];
    const destinations = streams
        .filter((stream) => {
            const key = normalizeFeatureFmDestinationKey(stream?.name);
            return key && !IGNORED_FEATURE_FM_DESTINATIONS.has(key);
        })
        .map((stream) => {
            const key = normalizeFeatureFmDestinationKey(stream.name);
            return {
                key,
                name: String(stream.name),
                clicks: toNullableNumber(stream.clicksToService) ?? 0,
                isTicket: isTicketDestinationKey(key),
            };
        });

    const total = destinations.reduce((sum, destination) => sum + destination.clicks, 0);

    return destinations
        .sort((a, b) => b.clicks - a.clicks)
        .map((destination) => ({
            ...destination,
            percent: total > 0 ? (destination.clicks / total) * 100 : 0,
        }));
};

export const shouldShowFeatureFmBreakdown = ({
    campaign,
    destinationUrl,
    smartLinkAnalytics,
    conversionEvent,
    hasFeatureFmAccess = true,
} = {}) => {
    if (hasFeatureFmAccess === false) {
        return false;
    }
    if (getConversionTrackingSource({ campaign, destinationUrl, hasFeatureFmAccess }) !== TRACKING_SOURCES.FEATURE_FM) {
        return false;
    }
    if (smartLinkAnalytics?.status === 'error' || smartLinkAnalytics?.status === 'skipped') {
        return false;
    }
    if (conversionEvent === CONVERSION_EVENTS.PRE_SAVE) {
        return Boolean(smartLinkAnalytics?.preSaves);
    }
    return getFeatureFmDestinationBreakdown(smartLinkAnalytics, conversionEvent).length > 0;
};

export const shouldGateFeatureFmBreakdown = ({ campaign, destinationUrl, hasFeatureFmAccess = true } = {}) => {
    if (hasFeatureFmAccess !== false) return false;
    return isFeatureFmTrackedDestination({ campaign, destinationUrl });
};

const STREAMING_BREAKDOWN_COPY = {
    title: 'Streaming Clicks by Platform',
    description: 'These numbers show the clicks that converted to your chosen streaming services. They are typically lower than overall ad link clicks, since they depend on how many people clicked through your landing page.',
};

const BIO_BREAKDOWN_COPY = {
    title: 'Clicks by Destination',
    description: 'These numbers show clicks from your bio page to destinations such as Spotify, Instagram, YouTube, merch, websites, and other links. Visiting the bio page itself is not a conversion — only a click on a destination inside it counts.',
};

const EVENT_BREAKDOWN_COPY = {
    title: 'Ticket Clicks vs Engagement Clicks',
    description: 'Only clicks to ticket destinations (Ticketmaster, Bandcamp, Eventbrite, Songkick, Ticketfly, Tour Date) count as conversions. Other downstream clicks are engagement and are excluded from Cost per Conversion.',
};

const PRE_SAVE_BREAKDOWN_COPY = {
    title: 'Pre-saves by Platform',
    description: 'These numbers show successful pre-saves on each streaming service, not landing-page clicks. Cost per Conversion uses this pre-save count.',
};

export const getFeatureFmBreakdownCopy = (conversionEvent) => {
    if (conversionEvent === CONVERSION_EVENTS.BIO_LINK) {
        return BIO_BREAKDOWN_COPY;
    }
    if (conversionEvent === CONVERSION_EVENTS.TICKET) {
        return EVENT_BREAKDOWN_COPY;
    }
    if (conversionEvent === CONVERSION_EVENTS.PRE_SAVE) {
        return PRE_SAVE_BREAKDOWN_COPY;
    }
    return STREAMING_BREAKDOWN_COPY;
};

export const getCampaignConversionSettings = ({ campaign, destinationUrl, linkType } = {}) => {
    const conversionEvent = getCampaignConversionEvent({ campaign, destinationUrl, linkType });
    const copy = getCampaignPerformanceCopy(conversionEvent);
    return {
        conversionsEnabled: true,
        conversionEvent,
        goal: copy.goal,
        conversionTitle: 'Conversions',
        conversionCountLabel: copy.countLabel,
        conversionCostLabel: copy.costLabel,
        conversionDescription: copy.description,
    };
};
